/**
 * Микроурок дня — экран этапа S11, §10.14 ТЗ.
 *
 * Урок приходит в чат перед сменой; здесь его можно перечитать и ответить
 * на вопрос, если в чате не успели. Ответ на вопрос — не экзамен: сервер
 * говорит «лучше так», а не «неправильно».
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Empty, ErrorState, Loading } from '@shell/index';
import { studyApi } from '../api';

export function Micro() {
  const today = useQuery({ queryKey: ['study', 'mentor', 'micro'], queryFn: studyApi.microToday });
  const [reply, setReply] = useState<string | null>(null);
  const answer = useMutation({
    mutationFn: ({ id, option }: { id: number; option: number }) =>
      studyApi.answerMicro(id, option),
    onSuccess: (result) => setReply(result.text),
    onError: (error) => setReply(error instanceof Error ? error.message : 'Не получилось.'),
  });

  if (today.isLoading) return <Loading />;
  if (today.error || !today.data) {
    return <ErrorState error={today.error} onRetry={() => today.refetch()} />;
  }
  const lesson = today.data.delivery;
  if (!lesson) {
    return (
      <Empty
        title="Микроуроков пока не было"
        text="Микроурок приходит в чат перед сменой, раз в день. Первый появится здесь, когда управляющий опубликует уроки."
      />
    );
  }

  return (
    <Card title={`☀️ ${lesson.topic}`}>
      <p className="faint">{new Date(lesson.sent_on).toLocaleDateString('ru-RU')}</p>
      <p>{lesson.body}</p>
      <p>
        <b>Как сказать:</b>
      </p>
      <ul>
        {lesson.good_phrases.map((phrase) => (
          <li key={phrase}>{phrase}</li>
        ))}
      </ul>
      {lesson.avoid_phrases.length > 0 && (
        <>
          <p>
            <b>Чего не говорить:</b>
          </p>
          <ul>
            {lesson.avoid_phrases.map((phrase) => (
              <li key={phrase}>{phrase}</li>
            ))}
          </ul>
        </>
      )}
      {lesson.question && (
        <>
          <p>
            <b>❓ {lesson.question}</b>
          </p>
          <div className="roleplay-scenarios">
            {lesson.options.map((option, index) => (
              <Button
                key={option}
                variant="quiet"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ id: lesson.id, option: index + 1 })}
              >
                {option}
              </Button>
            ))}
          </div>
        </>
      )}
      {reply && <p className="answer">{reply}</p>}
    </Card>
  );
}
